import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, Search } from 'lucide-react';
import Header from './components/Header18';
import Footer from './components/Footer18';
import Button from './components/Button';

const Orders = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    const orders = [
        {
            id: 'ORD-001',
            date: '2024-01-15',
            status: 'delivered',
            total: 6497,
            items: [
                { name: 'Minimalist Tote Bag', quantity: 1, image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=200' },
                { name: 'Classic White Shirt', quantity: 2, image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=200' },
            ],
            trackingNumber: 'TRK123456789',
        },
        {
            id: 'ORD-002',
            date: '2024-01-20',
            status: 'shipped',
            total: 3999,
            items: [
                { name: 'Black Sneakers', quantity: 1, image: 'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=200' },
            ],
            trackingNumber: 'TRK987654321',
            expectedDelivery: '2024-01-25',
        },
        {
            id: 'ORD-003',
            date: '2024-01-22',
            status: 'processing',
            total: 2299,
            items: [
                { name: 'Canvas Backpack', quantity: 1, image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=200' },
            ],
        },
    ];

    const statusConfig = {
        processing: {
            icon: Clock,
            label: 'Processing',
            color: 'text-yellow-600',
            bg: 'bg-yellow-50',
        },
        shipped: {
            icon: Truck,
            label: 'Shipped',
            color: 'text-blue-600',
            bg: 'bg-blue-50',
        },
        delivered: {
            icon: CheckCircle,
            label: 'Delivered',
            color: 'text-green-600',
            bg: 'bg-green-50',
        },
    };

    const filteredOrders = orders.filter(order => {
        const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.items.some(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesFilter = filterStatus === 'all' || order.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="min-h-screen bg-white">
            <Header showBackButton={true} cartCount={3} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-8">My Orders</h1>

                {/* Search and Filter */}
                <div className="flex flex-col md:flex-row gap-4 mb-8">
                    <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search orders..."
                            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900"
                        />
                    </div>

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-6 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900"
                    >
                        <option value="all">All Orders</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                    </select>
                </div>

                {/* Orders List */}
                {filteredOrders.length === 0 ? (
                    <div className="text-center py-16">
                        <Package size={64} className="text-gray-300 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">No orders found</h2>
                        <p className="text-gray-600 mb-6">Start shopping to see your orders here</p>
                        <Button variant="primary" onClick={() => navigate('/template18/shop')}>
                            Start Shopping
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {filteredOrders.map(order => {
                            const statusInfo = statusConfig[order.status];
                            const StatusIcon = statusInfo.icon;

                            return (
                                <div key={order.id} className="border border-gray-200 rounded-lg p-6">
                                    {/* Order Header */}
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 pb-6 border-b border-gray-200">
                                        <div>
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-lg font-bold text-gray-900">{order.id}</h3>
                                                <span className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bg} ${statusInfo.color}`}>
                                                    <StatusIcon size={16} />
                                                    {statusInfo.label}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600">Ordered on {new Date(order.date).toLocaleDateString()}</p>
                                            {order.expectedDelivery && (
                                                <p className="text-sm text-gray-600">Expected delivery: {new Date(order.expectedDelivery).toLocaleDateString()}</p>
                                            )}
                                        </div>
                                        <div className="mt-4 md:mt-0">
                                            <p className="text-2xl font-bold text-gray-900">₹{order.total}</p>
                                        </div>
                                    </div>

                                    {/* Order Items */}
                                    <div className="space-y-4 mb-6">
                                        {order.items.map((item, index) => (
                                            <div key={index} className="flex gap-4">
                                                <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                                    <img
                                                        src={item.image}
                                                        alt={item.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="font-semibold text-gray-900">{item.name}</h4>
                                                    <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-wrap gap-3">
                                        {order.trackingNumber && (
                                            <Button variant="outline">
                                                <Truck size={16} className="mr-2" />
                                                Track Order
                                            </Button>
                                        )}
                                        <Button variant="outline">View Details</Button>
                                        {order.status === 'delivered' && (
                                            <Button variant="primary">Buy Again</Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Orders;
